import type { Product } from './types'

export type ProductStockDisplay = {
  available: boolean
  visible: boolean
  label: string
  quantity: number | null
}

export const shouldShowOnlyAvailableFilter = (stockMode: number) => stockMode !== 0

const formatStockQuantity = (value: number) => (
  Number.isInteger(value)
    ? String(value)
    : new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 }).format(value)
)

export const resolveProductStockDisplay = (
  product: Pick<Product, 'inStock' | 'stockQuantity'>,
  showRealStock = false,
  stockMode = 2
): ProductStockDisplay => {
  const quantity = typeof product.stockQuantity === 'number' && Number.isFinite(product.stockQuantity)
    ? product.stockQuantity
    : null

  if (stockMode === 0) {
    return {
      available: true,
      visible: false,
      label: '',
      quantity,
    }
  }

  if (product.inStock === false || (quantity !== null && quantity <= 0)) {
    return {
      available: false,
      visible: true,
      label: 'Indisponível',
      quantity: quantity ?? 0,
    }
  }

  if (quantity !== null && quantity > 0 && showRealStock) {
    return {
      available: true,
      visible: true,
      label: `Disponível · ${formatStockQuantity(quantity)}`,
      quantity,
    }
  }

  return {
    available: true,
    visible: true,
    label: 'Disponível',
    quantity,
  }
}

export const isProductUnavailable = (product: Pick<Product, 'inStock' | 'stockQuantity'>, stockMode = 2) =>
  !resolveProductStockDisplay(product, false, stockMode).available

export const compareProductsByAvailability = (
  left: Pick<Product, 'inStock' | 'stockQuantity'>,
  right: Pick<Product, 'inStock' | 'stockQuantity'>,
  stockMode = 2
) => {
  const leftUnavailable = isProductUnavailable(left, stockMode)
  const rightUnavailable = isProductUnavailable(right, stockMode)

  if (leftUnavailable === rightUnavailable) return 0
  return leftUnavailable ? 1 : -1
}

export const sortProductsByAvailability = <T extends Pick<Product, 'inStock' | 'stockQuantity'>>(
  products: readonly T[],
  stockMode = 2
): T[] => (
  products
    .map((product, index) => ({ product, index }))
    .sort((left, right) => (
      compareProductsByAvailability(left.product, right.product, stockMode)
      || left.index - right.index
    ))
    .map(({ product }) => product)
)

export const filterUnavailableProductsWhenStockRuleEnabled = <
  T extends Pick<Product, 'inStock' | 'stockQuantity'>
>(
  products: readonly T[],
  stockMode = 2
): T[] => (
  stockMode === 0
    ? [...products]
    : products.filter((product) => !isProductUnavailable(product, stockMode))
)

export const filterHiddenUnavailableProducts = <
  T extends Pick<Product, 'inStock' | 'stockQuantity'>
>(
  products: readonly T[],
  stockMode = 2
): T[] => (
  stockMode === 1
    ? products.filter((product) => !isProductUnavailable(product, stockMode))
    : [...products]
)
